// Every word the Products, Add a product, product settings and Page layout screens show
// (AdminProducts.dc.html, AdminAddProduct.dc.html, AdminLayout.dc.html). Board words exactly, except
// where a rule, a decision or Adam's answer changes them; those changes, and every line no board
// draws, are in the stage 5 batch for Adam. Pure, so the spec can check the words on their own.
import { labelFromStripeName } from '../ui/format.js'

export const PRODUCTS = {
  eyebrow: 'Everything a member can be given',
  title: 'Products',
  add: 'Add a product',
  paidH2: 'Paid',
  paidAside: 'Only the people who bought it, or you gave it to, see it.',
  freeH2: 'Free with every account',
  freeAside: 'Every account sees these. Nobody buys them.',
  free: 'Free',
  live: 'Live',
  draft: 'Draft',
  notMovedIn: 'Not moved in',
  checklist: n => `Welcome checklist, ${n === 1 ? '1 step' : `${n} steps`}`,
  certificate: on => `Certificate: ${on ? 'on' : 'off'}`,
  layout: 'Page layout',
  duplicate: 'Duplicate',
  settings: 'Settings',
  seeAsMember: 'See it as a member',
  switchOn: name => `Switch on ${name}`,
  switchOff: name => `Switch off ${name}`,
  openSite: host => `Open ${host}`,
  empty: 'No products yet. Add 1 to start.',
  freeEmpty: 'No free items yet.',
  // the Duplicate flash (the board's green bar is gold here: a brand leak), naming the weeks or
  // modules that came across with it
  copied: (name, { weeks = 0, modules = 0 } = {}) => {
    const parts = weeks ? `, and its ${weeks === 1 ? '1 week' : `${weeks} weeks`}` : modules ? `, and its ${modules === 1 ? '1 module' : `${modules} modules`}` : ''
    return `Copied as a draft: ${name} (copy). Same type, look, page layout and checklist${parts}. No members, dates or prices yet.`
  },
  copiedPart: name => `Copied as a draft: ${name} (copy), but its checklist or page layout didn't all copy. Check them before you switch it on.`,
  close: 'Close',
  // the Live and Draft switch asks first (the brief; no board draws the confirm)
  confirmOff: name => `Switch off ${name}? Everyone who has it loses it straight away, until you switch it back on. Their progress is kept.`,
  confirmOffFree: name => `Switch off ${name}? It goes from every account straight away, until you switch it back on.`,
  confirmOn: name => `Switch on ${name}? Everyone who has it sees it in their library straight away.`,
  confirmOnFree: name => `Switch on ${name}? Every account sees it straight away.`,
  switchItOff: 'Switch it off',
  switchItOn: 'Switch it on',
  keepItOn: 'Keep it on',
  notYet: 'Not yet',
  ownLookFails: "Its own look fails the contrast check, so it can't be switched on. Fix its colours in its settings first.",
  // members' pages can't draw an own look yet, so none goes live (Adam, 5 Oct, question 8); the database
  // refuses it with the same words
  ownLookNotDrawn: "Its own look isn't drawn on members' pages yet, so it can't be switched on.",
  switchedOn: name => `${name} is live.`,
  switchedOff: name => `${name} is a draft again.`,
}

// The welcome checklist panel (AdminProducts.dc.html)
export const CHECKLIST = {
  line: name => `Members see this the first time they open ${name}. It hides once they've ticked everything, or pressed Hide.`,
  empty: 'No checklist. Members go straight in.',
  newLabel: 'New checklist item',
  placeholder: 'Add a step, e.g. Put the session dates in your calendar',
  add: 'Add step',
  remove: 'Remove',
  moveUp: text => `Move up: ${text}`,
  // what the board's text-only editor doesn't show: an existing step's button and who it shows to,
  // kept as they are (the map's recommendation)
  button: label => `Button: ${label}`,
  runningIntake: 'Only to members whose intake is running',
  noRunningIntake: 'Only to members with no intake running',
  tooLong: 'A step is at most 200 characters.',
  // undrawn: the 2 words a step can hold that each member reads as their own (src/product/checklist.js)
  placeholders: { group: '[their group]', next_session: '[their next session]' },
  placeholdersHint: 'Write {group} or {next_session} in a step and each member reads their own group and next session.',
}

// The certificate panel (AdminProducts.dc.html). "See what members get" isn't built: staff can't
// open a product's certificate page, so the link would go nowhere (a departure).
export const CERTIFICATE = {
  give: name => `Give a certificate when someone completes ${name}`,
  completeWhen: 'Complete when',
  rules: [
    { value: 'all_watched', label: 'Every session watched' },
    { value: 'all_watched_and_homework', label: 'Every session watched and all homework ticked' },
    { value: 'attended_min', label: 'Came to at least 5 live sessions' },
    { value: 'by_hand', label: 'You mark it by hand' },
  ],
  on: name => `The certificate shows their name, ${name}, the dates, a number and a link anyone can use to check it. It's issued when they meet the rule above, and the course-finished alert goes to you at the same time.`,
  off: 'Off. Nobody gets a certificate for this product.',
  byHand: "You can also issue 1 by hand from a person's page.",
}

// "See it as a member": view as someone who has it (lib/viewAs.jsx). No board draws the picker.
export const SEE_AS = {
  title: name => `See ${name} as a member`,
  line: "Pick a person who has it. You see the hub as that person sees it, for up to 2 hours, and nothing you do changes anything.",
  draft: name => `${name} is a draft, so no member sees it yet. Switch it on first, then view as someone who has it.`,
  nobody: "Nobody has it yet, so there's nobody to view as.",
  viewAs: first => `View as ${first}`,
  people: 'Find someone in People',
  loading: 'Loading.',
}

// The 3 panels at the foot of Products (AdminProducts.dc.html)
export const RULES_H2 = 'Who sees what'
export const RULES = [
  // "There is no free sign-up" with a contraction (Adam's writing rules)
  { head: 'Accounts come from buying, or from you', body: "Buying anything makes the account. You can also add someone by hand from People. There's no free sign-up." },
  { head: 'Free: every account sees it', body: "From the moment it goes live. On a person's page you can hold 1 back from them if you ever need to." },
  { head: 'Paid: only the people who have it', body: "Bought it, or you gave it to them. Buying adds it to their account straight away. Nobody sees a paid product they don't have, not even as a locked tile." },
  // any masterclass made in Add a product has its own offer at its own minute (the review of 13 Oct)
  { head: 'Offers hide from owners', body: "The hub sells in 2 places: each masterclass's offer, at the minute set for it (1:35:00 on the video), and a webinar replay's call to action. Each hides from anyone who already has what it sells." },
]

export const TYPES_H2 = 'Product types'

export const MOVES_H2 = 'Old addresses'
export const MOVES_LINE = 'Slides, emails and bookmarks still point at these, so each one forwards into the hub instead of breaking.'
export const MOVE_STATE = { live: 'Forwarding now', waiting: 'Not yet' }

// The product settings page (no board: the map's recommendation, the critic's batch)
export const SETTINGS = {
  title: 'Settings',
  crumbs: 'Products',
  notFoundTitle: 'Settings',
  notFound: "There's no product at this address.",
  back: 'Back to Products',
  aboutH2: 'About it',
  aboutAside: 'What members see on the tile and at the top of the product',
  name: 'Name',
  tagline: 'The line under the name',
  address: 'Web address',
  freeAddress: 'learn.adamslee.co.uk/free-resources',
  kind: 'Type',
  kindLine: (type, access) => `${type}, ${access === 'free' ? 'free' : 'paid'}. Fixed once it's switched on.`,
  cover: 'Cover photo',
  coverNone: 'No cover photo. The tile shows its look instead.',
  coverHas: 'It has a cover photo.',
  coverAdd: 'Add a cover photo',
  coverChange: 'Change the cover photo',
  coverHint: 'Your own photos only. It fills the top of the tile. A JPEG, PNG or WebP, at most 5 MB.',
  coverDone: 'Cover photo saved.',
  save: 'Save changes',
  saving: 'Saving.',
  saved: 'Saved.',
  nameNeeded: 'Give it a name first.',
  termsH2: 'Questions and terms',
  termsAside: 'What members read about where to ask and how long it lasts',
  questions: 'Where questions go',
  questionsHint: 'The questions line on its pages. The masterclass and Profit On Your Terms use course@adamslee.co.uk.',
  questionsBad: 'Type an email address, like course@adamslee.co.uk.',
  termsLine: 'How long it lasts',
  termsHint: "The line under it on Account's My programmes.",
  // the product's terms and conditions link (settings.terms_url): shown beside its Resources, and a copy
  // keeps the original's (the product-types follow-ups, 13 Oct)
  termsUrl: 'Terms and conditions link',
  termsUrlHint: name => `Members see "${name} terms and conditions" on its Resources page. A copy keeps the link of the product it came from. Leave it blank for none.`,
  termsUrlBad: 'A link starts https://, like https://poyt.adamslee.co.uk/terms.',
  minimum: 'The minimum the terms promise',
  minimumNone: 'No minimum',
  minimumMonths: n => (n === 1 ? '1 month' : `${n} months`),
  from: 'Counted from',
  fromPurchase: 'The purchase',
  fromStart: 'When their access starts',
  membership: n => `A ${n}-month membership, at the price on the day they join.`,
  audience: 'Who sees live sessions',
  audienceIntake: 'The intake they bought',
  audienceEveryone: 'Everyone who has it',
  audienceHint: 'The recordings go to everyone who has it either way.',
  whatsapp: 'WhatsApp link',
  whatsappHint: "Message Adam on WhatsApp, shown only to an intake while it's running. Everyone else gets the questions line.",
  whatsappBad: 'A WhatsApp link starts https://, like https://wa.me/447700900000.',
  prefix: 'Certificate numbers start',
  prefixHint: p => `Certificates are numbered like ${p || 'HUB'}-3-0042: these letters, the intake's number, then the count. Up to 6 capital letters; left blank, they start HUB.`,
  prefixBad: 'Up to 6 capital letters, like FE.',
  runsH2: 'How the hub runs it',
  runsAside: 'These change only when a product moves in, with your yes',
  runsAsideNew: 'Only an owner makes its Stripe purchases live',
  mode: 'Stripe purchases',
  modeLive: 'Live: a purchase opens it.',
  modeShadow: 'Shadow: the hub records what it would do, and opens nothing.',
  accessEmail: 'The access email',
  accessEmailOn: 'On: each buyer gets an email saying how to log in.',
  accessEmailOff: 'Off: the hub sends no access email.',
  where: 'Where it lives',
  whereIn: 'In the hub.',
  whereOut: host => `On ${host} until it moves. The hub reads who has it from there, only to hide the offer from them.`,
  runsNote: "The owner switches these at each product's move, in the same sitting as that step's yes, so they're shown here and not changed.",
  // a product made in Add a product or by Duplicate sells through Stripe once its purchases are live
  // (decision 2 of the product-types plan, 13 Oct); only an owner makes them live
  runsNoteNew: 'A product made here sells through Stripe once its purchases are live.',
  // a product made here that's paid monthly: the plan's step 8.4 reads the Accelerator's old payment flows first
  runsNoteMonthly: "It's paid monthly, so make its purchases live only once the Accelerator's old payment flows (stripe-webhook and n8n's Payment Status Sync) are checked. Until then, give it by hand from a person's page in People.",
  // a private client space made here: nothing about it is sold
  runsAsideClient: 'Nobody buys it in the hub',
  runsNoteClient: "You open each client's space by hand in People, with no Stripe price.",
  makeLive: 'Make Stripe purchases live',
  makeLiveConfirm: name => `Make purchases of ${name} live? From then on, buying 1 of its prices opens it once it's switched on, and the hub emails the buyer how to log in. Once someone has bought it, purchases can't go back to shadow.`,
  makeLiveYes: 'Make them live',
  makeLiveNo: 'Not yet',
  madeLive: 'Stripe purchases are live.',
  // a mastermind's guarantee (catalogue.settings.guarantee; the product-types follow-ups, 13 Oct): for owner and admin
  // only (Adam, 18 Oct: "Members don't need to see the rules; it's in the terms of service"). The switch says only
  // whether members see the on track pill on their home; the Accelerator's is on as it always has been, and a new or
  // copied mastermind's is off until it's switched on here
  guaranteeH2: 'The guarantee',
  guaranteeAside: 'For owner and admin only. Members never see the guarantee or its rules.',
  guaranteeShow: "Show members whether they're on track",
  guaranteeState: on => (on ? 'On' : 'Off'),
  guaranteeOff: "Members see their attendance only, with no on track pill, until it's switched on.",
  // the pill is the member's accountability log's state, by the numbers in Masterminds (the release of 14 Oct); once
  // they've slipped, the refocus card says it in its place (the Accelerator critique's member 3, 20 Oct)
  guaranteeHint: () => "On their home page, a pill under their attendance says they're on track, or the refocus card says it once they've slipped, and either opens their accountability log, with any sessions missed and recordings not watched. The guarantee's numbers are set in Masterminds, under Guarantee rules, and members never see them.",
  // a mastermind made in admin: its membership length, in place of the read-only line the Accelerator keeps
  membershipLength: 'Membership length',
  membershipMonths: n => (n === 1 ? '1 month' : `${n} months`),
  notMovedH2: 'Not in the hub yet',
  notMovedLine: (name, host) => `${name} is on ${host} until it moves. Its settings here come with the move.`,
  cardH2: 'Its card on Free resources',
  cardAside: 'Every account sees this card once it\'s switched on',
  blurb: 'What it is',
  buttonLabel: 'The button',
  buttonNote: 'The line under the button',
  linkUrl: 'The address it opens',
  linkBad: 'An address starts https://.',
  joinRoute: "Joins in 1 click through the Restart Challenge's own route. A new route needs a code change.",
  packFiles: 'Its files are in Resources and files.',
  packLink: 'Edit the files',
  // changed (stage 6, Adam, 5 Oct): the hub never connects to Zoom, so webinars are added by hand
  webinarsLine: "Each webinar is added by hand in Recordings and dates, Webinars, and stays off until it's switched on there.",
  webinarsLink: 'Webinars',
  lookH2: 'Look',
  lookAside: 'The colours and heading font inside the product',
  lookLive: name => `${name}. A product keeps its look once it's switched on, so its pages always match.`,
  recordingH2: 'The recording',
  recordingLine: (title, length) => `${title}, ${length}.`,
  recordingNone: 'No recording yet.',
  recordingLink: 'Change it in Recordings and dates',
}

// The masterclass offer and the owner's card: owner only, read only for an admin (Adam, 3 Oct; C14,
// C18). No board draws an editor.
export const OFFER = {
  h2: "The offer and the owner's card",
  aside: 'Only an owner changes these',
  adminNote: "Only an owner can change the offer and the owner's card. You can see them here.",
  on: 'Show the offer',
  onState: on => (on ? 'On' : 'Off'),
  at: 'It appears at',
  atHint: 'Hours, minutes and seconds into the recording, like 1:35:00.',
  atBad: 'Type a time like 1:35:00.',
  sells: 'What it sells',
  sellsHint: 'The offer hides from anyone who has this, at any status, and the owner\'s card goes to those who\'ve paid in full.',
  sellsNone: 'Nothing',
  eyebrow: 'Eyebrow',
  heading: 'Heading',
  body: 'The paragraph',
  stack: "What's in it, 1 per line",
  prices: 'Price lines, 1 per line',
  joinLabel: 'Join button',
  joinUrl: 'Its link',
  secureLabel: 'Secure a place button',
  secureUrl: 'Its link',
  balance: 'The balance line',
  timing: 'If the timing is wrong',
  placeholder: 'Before they reach it',
  showNow: 'The Show it now link',
  cardH3: "The owner's card",
  cardTitle: 'Title',
  cardLine: 'Line',
  cardButton: 'Button',
  cardUrl: 'Its link',
  cardUrlHint: 'An https:// address, or a page in the hub like /poyt once it moves in.',
  urlBad: 'A link starts https://.',
  cardUrlBad: 'A link starts https:// or /.',
  save: 'Save the offer',
  saved: "The offer and the owner's card are saved.",
  none: 'Not set',
  empty: '(blank)',
}

// Add a product (AdminAddProduct.dc.html), as Adam answered on 4 Oct (answer 8), with every type made
// here since 13 Oct (the product-types plan): nothing greyed out, and no line promising a code change
export const ADD = {
  crumbs: 'Products',
  title: 'Add a product',
  steps: [['Type', 'What it is'], ['Details', 'Name and address'], ['Look', 'Colours and cover'], ['Access', 'Who sees it'], ['Content', 'What goes inside']],
  stepNav: 'Steps',
  stepOf: (n, name) => `Step ${n} of 5: ${name}`,
  typeH2: 'What kind of product is it?',
  typeLine: 'The type decides what the member sees inside it. You can add pieces later.',
  detailsH2: 'Details',
  detailsLine: 'What members see on the tile and at the top of the product.',
  name: 'Name',
  namePlaceholder: 'e.g. The Portfolio Engine',
  line: 'The line under the name',
  linePlaceholder: 'e.g. Cohort 1',
  address: 'Web address',
  addressTaken: 'Another product or page already has this address, so it gets a number on the end.',
  questions: 'Where questions go',
  lookH2: 'Look',
  lookLine: 'The login, library and account stay in the master brand: white, near-black text, gold only as a small mark. This is the look inside the product.',
  lookNotes: {
    master: 'The default for anything new. Gold stays a small mark and never carries text.',
    own: 'Its own colours and heading font. The contrast check below has to pass before you can switch it on.',
    family: 'Only for a product in that family, like Accelerator Plus with the Accelerator. Anything else gets the master brand or its own look.',
  },
  // a type whose pages are drawn in 1 look only (decision 1 of the product-types plan)
  lookFixed: {
    mastermind: "A mastermind's pages are drawn in the Accelerator family, so it uses that look.",
    on_demand: "An on-demand course's pages are drawn in the Accelerator family, as Property Cashflow For Life's are, so it uses that look.",
    masterclass: "A masterclass's page is drawn in the Profit On Your Terms family, so it uses that look.",
    client_space: 'A private client space uses the master brand, as the 1-2-1 Mentorship does.',
  },
  background: 'Background',
  accent: 'Accent, for buttons',
  text: 'Text',
  headingFont: 'Heading font',
  fontNote: 'Body text stays in a plain font that reads well small. Any Google font can be added to this list.',
  contrastH3: 'Contrast check',
  contrastText: 'Text on the background',
  contrastButton: ink => `Button text on the accent, in ${ink}`,
  contrastEdge: 'Buttons against the background',
  contrastResult: (r, pass) => `${r} to 1, ${pass ? 'passes' : 'fails'}`,
  needsHex: 'Needs a 6-digit code',
  contrastPass: 'All 3 pass. Small text needs 4.5 to 1, buttons need 3 to 1 against the page.',
  contrastFail: "Something fails, so it saves as a draft but can't be switched on until it passes. Small text needs 4.5 to 1, buttons need 3 to 1 against the page.",
  coverAdd: 'Add a cover photo',
  coverAdded: name => `${name} added`,
  coverLine: 'Your own photos only. It fills the top of the tile.',
  coverBadType: 'A cover photo has to be a JPEG, PNG or WebP.',
  coverTooBig: 'That photo is bigger than 5 MB. Add a smaller one.',
  accessH2: 'Access',
  accessLine: 'Who sees it, and how they get in.',
  paid: ['Paid', 'Only people who buy it, or you give it to, see it.'],
  freeOpt: ['Free', 'Every account sees it. Nobody buys it.'],
  accessNote: {
    free: 'This type is always free.', paid: 'This type is always paid. Buying it adds it to their account straight away.', either: 'A resource pack can be either.',
    client_space: "Nobody buys it in the hub. You open each client's space by hand in People, with no Stripe price.",
  },
  howH3: 'How they get it',
  download: ['Download from the hub', 'The files open straight away. No emails follow.'],
  linkOut: ['Link out', 'Opens the address in a new tab. They sign up there, as everyone does.'],
  // a new linked item links out: 1-click join is the Restart Challenge's own route
  linkOnly: "It opens its own address in a new tab, where they sign up as everyone does. 1-click join is the Restart Challenge's own route.",
  linkAddress: 'The address it opens',
  linkPlaceholder: 'e.g. https://scorecard.adamslee.co.uk',
  linkBad: 'An address starts https://.',
  freeLine: 'Every account sees it as soon as you switch it on in Products. You can hold it back from 1 person on their page.',
  pricesH3: 'Stripe prices that open it',
  pricesNote: "Only prices that don't open anything yet are listed. Make the price in Stripe first, then tick it here.",
  pricesNone: "Every price in Stripe already opens something. Make the new price in Stripe first.",
  pricesLoading: "Reading Stripe's prices.",
  pricesAdmin: 'Only an owner maps prices. Map its price in the Price map once it\'s added.',
  priceName: (p, intervalWord) => [labelFromStripeName(p.product_name), p.display ? `${p.display}${intervalWord}` : null].filter(Boolean).join(', '),
  runsH3: 'Access runs',
  runs: [
    { value: 'membership', label: 'A 12-month membership, paid monthly' },
    { value: 'term', label: 'For as long as the course runs, and at least 12 months' },
    { value: 'subscription', label: 'While a subscription is paid' },
    { value: 'for_good', label: 'For good' },
  ],
  runsFrom: 'The 12 months count from',
  // its address already shows the hub's own login page (decision 3 of the product-types plan)
  loginLine: slug => `Members log in at learn.adamslee.co.uk/${slug} on the hub's own login page, then find it in their library.`,
  contentH2: 'Content',
  contentScheduled: 'Set the first date and the pattern. The dates fill in, and you can move any of them later.',
  contentPieces: 'Everything listed comes with it.',
  // the pieces that are Resources tabs it starts with (PIECES, 'tab'), the only ones ticked here
  tabsH3: 'Resources tabs it starts with',
  tabsLine: 'Untick any it doesn\'t need. You can add, rename or remove tabs later in Resources.',
  contentMastermind: 'Group 1 is made with it. Add its sessions, and more groups, in Recordings and dates, Groups and dates.',
  modules: 'Modules',
  modulesLine: 'Each module gets its recording in Recordings and dates. You can add more there later.',
  cohortStarts: 'Cohort 1 starts',
  day: 'Day',
  time: 'Time, UK',
  weeks: 'Weeks',
  scheduleLine: (first, day, at, n) => `Week 1 is ${first} at ${at}, then every ${day} for ${n === 1 ? '1 week' : `${n} weeks`}.`,
  scheduleSkip: 'Leave the date blank to add the dates later in Recordings and dates.',
  eachWeek: 'Each week has',
  inside: 'Inside it',
  tileH3: 'The tile in the library',
  tileName: 'Your product name',
  tileLine: 'The line under the name',
  tileNote: { paid: "Saved as a draft, nobody sees it. Switch it on in Products when it's ready.", free: 'Saved as a draft. When you switch it on, it appears under Free resources for every account.' },
  back: 'Back',
  next: name => `Next: ${name}`,
  create: 'Create as draft',
  creating: 'Creating.',
  needName: 'Give it a name first.',
  needTypeFirst: 'Pick a type first.',
  doneTitle: name => `${name} is saved as a draft`,
  // the done lines, by type (the product-types plan, 13 Oct)
  doneLine: "Nobody can see it until you switch it on in Products. Map its Stripe price in the Price map if you didn't tick one.",
  doneSells: "Once it's switched on, buying 1 of its ticked prices opens it, and the hub emails the buyer how to log in.",
  doneShadow: 'Its Stripe purchases are in shadow until the owner makes them live in its settings.',
  // a product paid monthly stays in shadow until the plan's step 8.4 (data.js createProduct)
  doneMonthly: "It's paid monthly, so its Stripe purchases stay in shadow until the Accelerator's old payment flows are checked. Until then, give it by hand from a person's page in People.",
  doneCohort: (cohort, n) => `${cohort} and its ${n === 1 ? '1 session are' : `${n} sessions are`} in Recordings and dates.`,
  doneWeeks: n => `Its ${n === 1 ? '1 week' : `${n} weeks`} and Cohort 1 are in Recordings and dates. Set the dates there.`,
  doneMastermind: 'Group 1 is in Recordings and dates, Groups and dates. Add its sessions there.',
  doneModules: n => `Its ${n === 1 ? '1 module is' : `${n} modules are`} in Recordings and dates. Add each module's recording there.`,
  doneMasterclass: 'Add its video in Recordings and dates, then set its offer in its settings.',
  doneMasterclassAdmin: 'Add its video in Recordings and dates. The owner sets its offer in its settings.',
  donePack: 'Add its files in Resources.',
  doneClientSpace: "Switch it on in Products, then open each client's space in People with Open a 1-2-1 space. Nothing is emailed.",
  doneFree: 'Saved as a draft. Switch it on in Products and every account sees it under Free resources.',
  doneCoverFailed: "The cover photo didn't upload. Add it from its settings.",
  donePriceFailed: "Its Stripe price didn't map. Map it in the Price map.",
  doneCohortFailed: "Its dates didn't all save. Add them in Recordings and dates.",
  doneWeeksFailed: "Its weeks didn't save, so there's nothing to add its recordings to. Add it again: this draft stays hidden.",
  doneModulesFailed: "Its modules didn't save. Add them in Recordings and dates with Add module.",
  doneGroupFailed: "Group 1 didn't save. Add it in Recordings and dates, Groups and dates.",
  doneTabsFailed: "Its Resources tabs didn't all save. Add them in Resources.",
  doneBillingFailed: 'Its Stripe purchases are still in shadow. Make them live in its settings.',
  goProducts: 'Go to Products',
  addAnother: 'Add another',
  settings: 'Open its settings',
}

// The types as Add a product and Products' panel describe them. The Linked line is corrected: the
// scorecard hands over nothing (the critic's missed 7).
export const TYPES = [
  {
    key: 'mastermind', name: 'Mastermind', access: 'Paid', like: 'Like The Property Accelerator',
    what: 'A group, sessions each month, a goal, actions, attendance and a guarantee.',
    panel: 'Monthly sessions in a group, a goal, actions each month, attendance and the guarantee.',
  },
  {
    key: 'live_cohort', name: 'Live cohort', access: 'Paid', like: 'Like The Funding Engine and Profit On Your Terms',
    what: 'Live sessions on set dates, then the recording, notes, homework and downloads each week.',
    panel: 'Weekly live sessions on set dates, then the recording, notes, homework and pack each week.',
  },
  {
    key: 'on_demand', name: 'On demand', access: 'Paid', like: 'Like Property Cashflow For Life sold on demand',
    what: 'Modules and recordings, open at once or released on a schedule.',
    panel: 'Modules and recordings, open at once or released on a schedule.',
  },
  {
    key: 'masterclass', name: 'Masterclass', access: 'Paid', like: 'Like Buy your next property without a deposit',
    what: '1 recording, with an offer that appears at a set minute.',
    panel: '1 recording, with an offer that appears at a set minute.',
  },
  {
    key: 'client_space', name: 'Private client space', access: 'Paid', like: 'Like the 1-2-1 Mentorship',
    what: '1 client and Adam: sessions, recordings, notes, actions, shared files and booking.',
    panel: '1 client and Adam: sessions, recordings, notes, actions, shared files and booking.',
  },
  {
    key: 'resource_pack', name: 'Resource pack', access: 'Paid or free', like: 'Like the 21 Ways guide',
    what: 'Downloads and tools, nothing live.',
    panel: 'Downloads and tools, nothing live.',
  },
  {
    // changed (stage 6): added by hand, not pulled from Zoom
    key: 'webinar', name: 'Webinar', access: 'Free', like: 'Schedule it in Zoom, then add it in Recordings and dates, Webinars',
    what: "Added by hand, registered on Zoom's own page, then the replay.",
    panel: "Added by hand and registered on Zoom's own page, then the replay until you take it down.",
    // not a product: its tile in Add a product opens Recordings and dates, Webinars, where each 1 is added
    link: 'Add it in Webinars',
  },
  {
    key: 'linked', name: 'Linked', access: 'Free', like: 'Like The Next Deal Scorecard',
    what: 'Lives somewhere else, like the scorecard or the challenge. The hub shows the tile, and a 1-click join hands over the name and email. The scorecard asks for them itself.',
    // Add a product's own words: a new linked item links out (1-click join is the Restart Challenge's own route)
    add: 'Lives somewhere else, like the scorecard. Its tile opens the address in a new tab, and they sign up there.',
    panel: 'Lives somewhere else, like the scorecard or the challenge. The hub shows the tile, and a 1-click join hands over the name and email. The scorecard asks for them itself.',
  },
]

// The 5 looks (AdminAddProduct.dc.html)
export const LOOK_NAMES = {
  master: ['Master brand', 'White, near-black text, a gold rule'],
  accelerator: ['The Accelerator family', 'Off-white and forest green'],
  funding_engine: ['The Funding Engine family', 'Black-green and bright green'],
  poyt: ['Profit On Your Terms family', 'Navy and burnt orange'],
  own: ['Its own', 'Its own colours and heading font'],
}

// What goes inside, per type (answer 8: only what the hub does; no zip, no join alert, no finish
// email to the member, pixel events waiting on C06). A resource pack's "Email when something new goes
// in" is gone (Adam, 5 Oct, stage 6: no notification emails; the bell says what's new).
//
// Add a product's Content step lists them as they come with every product of the type. Only a piece
// marked 'tab' or 'tab-off' (ticked, or not, to start with) is a tick box: it's a Resources tab the
// product starts with (model.js startingSections), so unticking it changes what's made. Nothing else
// reads them (the review of 13 Oct: a tick box that changes nothing isn't offered). So the list holds
// only what a new product of the type has: no module a week (an on-demand course's recordings open at
// once, or each on its own release rule), no bonus series (Property Cashflow For Life's own) and no 1-click
// join (the Restart Challenge's own route).
export const PIECES = {
  // a mastermind comes whole, as the Accelerator does: its groups, sessions, goal, actions and wins
  mastermind: [],
  live_cohort: [
    ['recording', 'Recording with chapters'], ['summary', 'Summary and key messages'], ['homework', 'Homework to tick'],
    ['worksheets', 'Worksheets to download', 'tab'], ['prompts', 'Prompts to copy', 'tab'], ['pack', 'A pack of scripts and templates each week', 'tab-off'],
  ],
  on_demand: [
    ['modules', 'Modules with recordings and chapters'], ['homework', 'Homework to tick'],
    ['progress', "How many modules they've done, on its home"], ['downloads', 'Downloads for each module', 'tab'],
  ],
  masterclass: [
    ['recording', "The recording, with the member's place saved"], ['offer', 'An offer that appears at a set minute'],
    ['hide_offer', 'The offer hidden from anyone who already has what it sells'], ['show_now', 'A link for returners to show the offer'],
  ],
  client_space: [
    ['sessions', 'Sessions with the Zoom link and Add to calendar'], ['notes', 'Recordings and notes after each session'],
    ['actions', 'Actions agreed each session, ticked by the client'], ['files', 'Shared files, both ways'],
    ['booking', 'A booking link for the next session'], ['ask', 'Ask Adam, a question box'],
  ],
  resource_pack: [
    ['shelves', 'Shelves of downloads'], ['tools', 'Calculators that open in the browser'],
  ],
  webinar: [],
  linked: [
    ['tile', 'A tile with a cover and 1 line'], ['button', 'The button that opens its address in a new tab'],
    ['next_line', 'A line under the button saying what happens next'], ['logged', 'Logged on their People page'],
  ],
}

// Page layout (AdminLayout.dc.html, phase 1)
export const LAYOUT = {
  title: 'Page layout',
  crumbs: 'Products',
  saveDraft: 'Save draft',
  publish: 'Publish',
  statusDraft: 'Draft, not published',
  statusPublished: 'Members see the published layout',
  draftSaved: 'Draft saved. Members still see the published layout.',
  published: 'Published. Members see the new layout the next time they open the page.',
  // "there is nothing to publish" with a contraction (Adam's writing rules)
  nothingShown: "Nothing is shown, so there's nothing to publish. Show at least 1 section.",
  backToDefault: 'Back to the default',
  defaultBack: 'The default layout is back as a draft. Publish it to show members.',
  info: "Each product type comes with a default layout. Here you choose which sections show and in what order, on the product's home page and on its week pages. Colours and fonts come from the product's look, so nothing you do here can make a page hard to read.",
  listFoot: 'Sections marked Always hold the course itself, so they can move but not hide. The same order is used on a phone.',
  always: 'Always',
  moveUp: name => `Move up: ${name}`,
  moveDown: name => `Move down: ${name}`,
  hide: name => `Hide ${name}`,
  show: name => `Show ${name}`,
  previewSize: 'Preview size',
  desktop: 'Desktop',
  phone: 'Phone',
  previewLaptop: 'Preview on a laptop',
  previewPhone: 'Preview on a phone',
  noteLaptop: 'What a member sees',
  notePhone: 'On a phone every section is 1 column, in the same order.',
  allHidden: 'Every section is hidden. Members would see an empty page, so Publish stays off until 1 is shown.',
  lookH2: 'The look',
  lookLine: name => `Colours, fonts and the cover come from ${name}'s look, set when the product was added. Every page of the product uses them, so the home page and the week pages always match.`,
  none: name => `${name} has no page layout to change.`,
  noneWhy: 'Page layout is for The Funding Engine and Property Cashflow For Life in phase 1: the other products have no layout their pages read, or are on holding pages until they move.',
  notFound: "There's no product at this address.",
  back: 'Back to Products',
}
